import {Maybe} from '@coolcolduk/typescript-util';
import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {z} from 'zod';
import {SeedQuestion, SeedQuestionStatus} from '@cp/model';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useCountQuestionsBySeedQuestion} from '../../../hooks/question/useCountQuestionsBySeedQuestion';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';
import {useListSeedQuestions} from '../../../hooks/seedQuestion/useListSeedQuestions';
import {useUpdateSeedQuestion} from '../../../hooks/seedQuestion/useUpdateSeedQuestion';

const seedQuestionParamsSchema = z.object({
  moduleId: z.string().optional(),
  topic: z.string().optional(),
  status: z.nativeEnum(SeedQuestionStatus).optional(),
  page: z.number().int().positive().optional(),
});

type SeedQuestionParams = z.infer<typeof seedQuestionParamsSchema>;

export interface UseAdminSeedQuestionPageReturn {
  seedQuestion: SeedQuestion | null;
  examModules: any[];
  moduleId: string | null;
  selectedModule: any | undefined;

  isLoading: boolean;
  modulesLoading: boolean;
  updateLoading: boolean;
  questionCount: number | null;
  questionCountLoading: boolean;
  navigationLoading: boolean;
  hasPrev: boolean;
  hasNext: boolean;

  formData: {
    questionText: string;
    topic: string;
    options: string[];
    answer: string;
    status: Maybe<SeedQuestionStatus>;
  };
  handleFormDataChange: (data: Partial<UseAdminSeedQuestionPageReturn['formData']>) => void;

  handleBack: () => void;
  handleSave: () => Promise<void>;
  handleFetchQuestionCount: () => Promise<void>;
  handlePrev: () => Promise<void>;
  handleNext: () => Promise<void>;
}

export default function useAdminSeedQuestionPage(): UseAdminSeedQuestionPageReturn {
  const {seedQuestionId} = useParams<{seedQuestionId: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {updateSeedQuestion, loading: updateLoading} = useUpdateSeedQuestion();
  const {loading: seedQuestionsLoading, fetchSeedQuestions} = useListSeedQuestions();
  const {countQuestionsBySeedQuestion, loading: questionCountLoading} = useCountQuestionsBySeedQuestion();
  const {showMessage} = useMessage();

  // Read parameters from URL
  const {searchParams} = useSearchParamsHandler<SeedQuestionParams>({
    schema: seedQuestionParamsSchema,
    defaultValues: {
      moduleId: '',
      topic: '',
      status: undefined,
      page: 1,
    },
  });

  const locationState = location.state as {seedQuestion?: SeedQuestion} | undefined;

  const [seedQuestion, setSeedQuestion] = useState<SeedQuestion | null>(locationState?.seedQuestion || null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const moduleId = searchParams.moduleId || null;
  const topic = searchParams.topic || '';
  const status = searchParams.status;
  const page = searchParams.page || 1;

  const [formData, setFormData] = useState<{
    questionText: string;
    topic: string;
    options: string[];
    answer: string;
    status: Maybe<SeedQuestionStatus>;
  }>(() => {
    if (locationState?.seedQuestion) {
      return {
        questionText: locationState.seedQuestion.questionText || '',
        topic: locationState.seedQuestion.topic || '',
        options: locationState.seedQuestion.options || [],
        answer: locationState.seedQuestion.answer || '',
        status: locationState.seedQuestion.status ?? SeedQuestionStatus.ACTIVE,
      };
    }
    return {
      questionText: '',
      topic: '',
      options: [],
      answer: '',
      status: SeedQuestionStatus.ACTIVE,
    };
  });

  const selectedModule = examModules.find((m) => m.id === moduleId);

  useEffect(() => {
    if (seedQuestion) {
      setFormData({
        questionText: seedQuestion.questionText || '',
        topic: seedQuestion.topic || '',
        options: seedQuestion.options || [],
        answer: seedQuestion.answer || '',
        status: seedQuestion.status ?? SeedQuestionStatus.ACTIVE,
      });
    }
  }, [seedQuestion]);

  useEffect(() => {
    if (!seedQuestion && seedQuestionId && moduleId && !loadingQuestion) {
      setLoadingQuestion(true);
      fetchSeedQuestions({moduleId})
        .then((rows) => {
          const found = rows.find((sq) => sq.id === seedQuestionId);
          if (found) {
            setSeedQuestion(found);
          } else {
            showMessage('Seed question not found', 6000, 'error');
          }
        })
        .catch(() => {
          showMessage('Failed to load seed question', 6000, 'error');
        })
        .finally(() => {
          setLoadingQuestion(false);
        });
    }
  }, [seedQuestion, seedQuestionId, moduleId, fetchSeedQuestions, loadingQuestion, showMessage]);

  const handleBack = useCallback(() => {
    if (moduleId) {
      // Build URL with query parameters from URL
      const params = new URLSearchParams();
      params.set('moduleId', moduleId);
      if (topic) params.set('topic', topic);
      if (status) params.set('status', status);
      if (page > 1) params.set('page', String(page));
      const queryString = params.toString();

      // Navigate to list page with refresh flag
      navigate(`/admin/seedQuestions?${queryString}`, {
        state: {refresh: true},
      });
    } else {
      navigate(-1);
    }
  }, [navigate, moduleId, topic, status, page]);

  const handleSave = useCallback(async () => {
    if (!seedQuestion || !moduleId) return;

    try {
      const updated = await updateSeedQuestion(
        moduleId,
        seedQuestion.id,
        formData.questionText || undefined,
        formData.topic || undefined,
        formData.options.length > 0 ? formData.options : undefined,
        formData.answer || undefined,
        undefined,
        formData.status || SeedQuestionStatus.ACTIVE,
      );

      if (updated) {
        setSeedQuestion(updated);
        showMessage('Seed question updated successfully', 6000, 'success');
      }
    } catch {
      showMessage('Failed to save seed question. Please try again.', 6000, 'error');
    }
  }, [seedQuestion, moduleId, formData, updateSeedQuestion, showMessage]);

  const handleFormDataChange = useCallback((data: Partial<UseAdminSeedQuestionPageReturn['formData']>) => {
    setFormData((prev) => ({...prev, ...data}));
  }, []);

  const handleFetchQuestionCount = useCallback(async () => {
    if (!seedQuestion || !moduleId) return;

    try {
      const count = await countQuestionsBySeedQuestion(moduleId, seedQuestion.id);
      setQuestionCount(count);
    } catch (error) {
      console.error('Failed to fetch question count:', error);
      showMessage('Failed to fetch question count', 3000, 'error');
    }
  }, [seedQuestion, moduleId, countQuestionsBySeedQuestion, showMessage]);

  // Fetch seed questions to determine prev/next availability
  useEffect(() => {
    if (!seedQuestion || !moduleId || !seedQuestionId) {
      setHasPrev(false);
      setHasNext(false);
      return;
    }

    const checkNavigation = async () => {
      try {
        // Fetch a larger set to find prev/next (fetch current page and adjacent pages)
        const pageSize = 10;
        const currentPageStart = (page - 1) * pageSize;

        // Fetch enough items to check prev/next (current page + 1 before + 1 after)
        const fetchStart = Math.max(0, currentPageStart - pageSize);
        const fetchLimit = pageSize * 3; // Current + prev + next pages

        const questions = await fetchSeedQuestions({
          moduleId,
          topic: topic || undefined,
          status: status || undefined,
          limit: fetchLimit,
          offset: fetchStart,
        });

        if (questions.length > 0) {
          const currentIndex = questions.findIndex((sq) => sq.id === seedQuestionId);

          if (currentIndex >= 0) {
            // Check if there's a previous question (either in current batch or previous page)
            const hasPrevInBatch = currentIndex > 0;
            const hasPrevInPreviousPage = fetchStart > 0 || hasPrevInBatch;
            setHasPrev(hasPrevInPreviousPage);

            // Check if there's a next question (either in current batch or next page)
            const hasNextInBatch = currentIndex < questions.length - 1;
            // If we got less than requested, we're at the end
            const hasMorePages = questions.length === fetchLimit;
            setHasNext(hasNextInBatch || hasMorePages);
          } else {
            setHasPrev(false);
            setHasNext(false);
          }
        }
      } catch (error) {
        console.error('Failed to check navigation:', error);
        setHasPrev(false);
        setHasNext(false);
      }
    };

    checkNavigation();
  }, [seedQuestion, seedQuestionId, moduleId, topic, status, page, fetchSeedQuestions]);

  const navigateToQuestion = useCallback(
    async (direction: 'prev' | 'next') => {
      if (!seedQuestion || !moduleId || !seedQuestionId || navigationLoading) return;

      setNavigationLoading(true);
      try {
        const pageSize = 10;
        const currentPageStart = (page - 1) * pageSize;

        // Determine which page range to fetch
        let fetchStart: number;
        let fetchLimit: number;

        if (direction === 'prev') {
          // Fetch previous page and current page
          fetchStart = Math.max(0, currentPageStart - pageSize);
          fetchLimit = pageSize * 2;
        } else {
          // Fetch current page and next page
          fetchStart = currentPageStart;
          fetchLimit = pageSize * 2;
        }

        const questions = await fetchSeedQuestions({
          moduleId,
          topic: topic || undefined,
          status: status || undefined,
          limit: fetchLimit,
          offset: fetchStart,
        });

        if (questions.length > 0) {
          const currentIndex = questions.findIndex((sq) => sq.id === seedQuestionId);

          if (currentIndex >= 0) {
            let targetQuestion: SeedQuestion | undefined;
            let targetPage = page;

            if (direction === 'prev') {
              if (currentIndex > 0) {
                // Previous question in same batch
                targetQuestion = questions[currentIndex - 1];
                // Calculate which page this question is on
                const questionGlobalIndex = fetchStart + currentIndex - 1;
                targetPage = Math.floor(questionGlobalIndex / pageSize) + 1;
              } else if (fetchStart > 0) {
                // Need to fetch earlier page
                const earlierFetchStart = Math.max(0, fetchStart - pageSize);
                const earlierRows = await fetchSeedQuestions({
                  moduleId,
                  topic: topic || undefined,
                  status: status || undefined,
                  limit: pageSize,
                  offset: earlierFetchStart,
                });
                if (earlierRows.length > 0) {
                  targetQuestion = earlierRows[earlierRows.length - 1];
                  targetPage = Math.floor(earlierFetchStart / pageSize) + 1;
                }
              }
            } else {
              // next
              if (currentIndex < questions.length - 1) {
                // Next question in same batch
                targetQuestion = questions[currentIndex + 1];
                // Calculate which page this question is on
                const questionGlobalIndex = fetchStart + currentIndex + 1;
                targetPage = Math.floor(questionGlobalIndex / pageSize) + 1;
              } else {
                // Need to fetch next page
                const nextFetchStart = fetchStart + questions.length;
                const nextRows = await fetchSeedQuestions({
                  moduleId,
                  topic: topic || undefined,
                  status: status || undefined,
                  limit: pageSize,
                  offset: nextFetchStart,
                });
                if (nextRows.length > 0) {
                  targetQuestion = nextRows[0];
                  targetPage = Math.floor(nextFetchStart / pageSize) + 1;
                }
              }
            }

            if (targetQuestion) {
              // Build URL with query parameters
              const params = new URLSearchParams();
              params.set('moduleId', moduleId);
              if (topic) params.set('topic', topic);
              if (status) params.set('status', status);
              if (targetPage > 1) params.set('page', String(targetPage));
              const queryString = params.toString();
              const url = `/admin/seedQuestion/${targetQuestion.id}${queryString ? `?${queryString}` : ''}`;

              navigate(url, {
                state: {
                  seedQuestion: targetQuestion,
                },
              });
            } else {
              showMessage(`No ${direction === 'prev' ? 'previous' : 'next'} question found`, 3000, 'info');
            }
          }
        }
      } catch (error) {
        console.error(`Failed to navigate ${direction}:`, error);
        showMessage(`Failed to navigate to ${direction === 'prev' ? 'previous' : 'next'} question`, 3000, 'error');
      } finally {
        setNavigationLoading(false);
      }
    },
    [
      seedQuestion,
      seedQuestionId,
      moduleId,
      topic,
      status,
      page,
      fetchSeedQuestions,
      navigate,
      showMessage,
      navigationLoading,
    ],
  );

  const handlePrev = useCallback(() => navigateToQuestion('prev'), [navigateToQuestion]);

  const handleNext = useCallback(() => navigateToQuestion('next'), [navigateToQuestion]);

  const isLoading = loadingQuestion || seedQuestionsLoading;

  return {
    seedQuestion,
    examModules,
    moduleId,
    selectedModule,
    isLoading,
    modulesLoading,
    updateLoading,
    questionCount,
    questionCountLoading,
    navigationLoading,
    hasPrev,
    hasNext,
    formData,
    handleFormDataChange,
    handleBack,
    handleSave,
    handleFetchQuestionCount,
    handlePrev,
    handleNext,
  };
}
